import { useEffect, useState } from "react";
import { Search, FileText, Upload, Download, X, Eye } from "lucide-react";
import { api, fileUrl } from "../../lib/api";
import { toast } from "sonner";

export default function PerfilesProfesores() {
    const [teachers, setTeachers] = useState([]);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null);

    async function load() {
        const r = await api.get("/users", { params: { role: "profesor" } });
        setTeachers(r.data || []);
    }
    useEffect(() => { load(); }, []);

    let filtered = teachers;
    if (search)
        filtered = filtered.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()));

    return (
        <div className="p-6 md:p-10 fade-in" data-testid="perfiles-profesores-page">
            <h1 className="text-3xl font-semibold mb-1">Perfiles de Profesores</h1>
            <p className="text-edu-body mb-6">Visualiza profesores y gestiona sus hojas de vida.</p>

            <div className="relative max-w-sm mb-6">
                <Search
                    size={16}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-edu-muted"
                />
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por nombre…"
                    className="edu-input w-full pl-10"
                    data-testid="search-teachers"
                />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {filtered.length === 0 && <p className="text-edu-muted col-span-2">Sin profesores.</p>}
                {filtered.map((t) => (
                    <div key={t.id} className="edu-card p-5" data-testid={`teacher-${t.id}`}>
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-xl bg-edu-navy/10 text-edu-navy flex items-center justify-center font-bold text-sm">
                                    {t.name.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold">{t.name}</h3>
                                    <p className="text-xs text-edu-muted">{t.email}</p>
                                </div>
                            </div>
                            {t.cv_file_id ? (
                                <span className="edu-pill bg-emerald-100 text-emerald-800">
                                    CV subido
                                </span>
                            ) : (
                                <span className="edu-pill bg-gray-100 text-gray-600">
                                    Sin CV
                                </span>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                onClick={() => setEditing(t)}
                                className="flex-1 px-3 py-2 text-xs rounded-lg bg-edu-navy text-white inline-flex items-center justify-center gap-1"
                                data-testid={`upload-cv-${t.id}`}
                            >
                                <Upload size={12} /> {t.cv_file_id ? "Reemplazar CV" : "Subir CV"}
                            </button>
                            {t.cv_file_id && (
                                <a
                                    href={fileUrl(t.cv_file_id)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex-1 px-3 py-2 text-xs rounded-lg border border-edu hover:bg-gray-50 inline-flex items-center justify-center gap-1"
                                    data-testid={`view-cv-${t.id}`}
                                >
                                    <Eye size={12} /> Ver CV
                                </a>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {editing && (
                <UploadCVModal
                    teacher={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => {
                        setEditing(null);
                        load();
                    }}
                />
            )}
        </div>
    );
}

function UploadCVModal({ teacher, onClose, onSaved }) {
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);

    async function upload() {
        if (!file) return;
        setSaving(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/files/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            await api.patch(`/users/${teacher.id}`, { cv_file_id: data.id });
            toast.success("CV subido correctamente");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Error subiendo CV");
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="edu-card p-6 max-w-md w-full">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold">Hoja de Vida — {teacher.name}</h3>
                    <button onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <p className="text-sm text-edu-body mb-4 inline-flex items-start gap-2">
                    <FileText size={14} className="text-edu-navy mt-0.5 flex-shrink-0" />
                    Sube un PDF con la hoja de vida del profesor. Estará visible para administrativos.
                </p>
                <input
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setFile(e.target.files?.[0])}
                    className="edu-input w-full mb-4"
                    data-testid="cv-file-input"
                />
                <div className="flex justify-end gap-3">
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-xl border border-edu">
                        Cancelar
                    </button>
                    <button
                        onClick={upload}
                        disabled={!file || saving}
                        className="edu-btn-primary px-4 py-2 text-sm"
                        data-testid="upload-cv-confirm"
                    >
                        {saving ? "Subiendo…" : "Subir CV"}
                    </button>
                </div>
            </div>
        </div>
    );
}
